// src/pages/equipmentrentaladmin/dispute/ResolveDisputes.jsx

import React, { useState, useEffect, useRef, useContext } from 'react';
import { Row, Col, Form, Tab, Nav } from 'react-bootstrap';
import { FaExclamationCircle, FaCheckCircle, FaClock, FaPaperPlane } from 'react-icons/fa';
import { FiMoreVertical, FiEye, FiTrash2, FiX, FiCheckCircle, FiSend } from 'react-icons/fi';
import RentalAdminLayout from '../../../layout/equipmentrentaladmin/RentalAdminLayout';
import '../../../assets/css/equipmentrentaladmin/dispute/RentalDispute.css';
import '../../../assets/css/equipmentrentaladmin/dashboard/RentalDashboard.css';
import AppPagination from '../../../components/general/pagination/Pagination.js';
import AppContext from '../../../context/AppContext.js';
import axios from 'axios';
import Swal from 'sweetalert2';

const ResolveDisputes = () => {
  const { baseUrl, loggedInRental, baseUrlForFile , authToken} = useContext(AppContext);

  // ── States ──
  const [disputes, setDisputes] = useState([]);
  const [stats, setStats] = useState({ total: 0, resolved: 0, inProgress: 0, urgent: 0 });
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [activeMenu, setActiveMenu] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [conversationId, setConversationId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [forwarding, setForwarding] = useState(false);

  const [activeTab, setActiveTab] = useState("myDisputes");
  const [customerDisputes, setCustomerDisputes] = useState([]);

  const messagesEndRef = useRef(null);
  const disputesPerPage = 5;
  const currentData =
    activeTab === "myDisputes"
      ? disputes
      : customerDisputes;

  const totalPages = Math.ceil(
    currentData.length / disputesPerPage
  );

  const currentDisputes = currentData.slice(
    (currentPage - 1) * disputesPerPage,
    currentPage * disputesPerPage 
  );

  // ── Fetch disputes ──
  const viewMyDisputes = () => {
    if (!loggedInRental) return;
    axios.post(`${baseUrl}/rentaladmin/dispute/viewMyDisputes`, {
      srrentaladmin_id: loggedInRental.id
    }, {
      headers: { Authorization: `Bearer ${authToken}` }
    }).then((res) => {
      console.log("viewMyDisputes:", res.data);
      if (res.data.success) setDisputes(res.data.request || []);
    }).catch((err) => console.log("API Error:", err));
  };

  useEffect(() => {
    console.log("Rental Admin ID:", loggedInRental?.id);

    if (loggedInRental?.id) {
      viewMyDisputes();
      viewCustomerDisputes();
    }
  }, [loggedInRental]);

  // ── Stats ──
  useEffect(() => { calculateStats(); }, [disputes]);

  // ── Scroll to bottom on new message ──
  useEffect(() => {
    if (showModal && messagesEndRef.current)
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, showModal]);

  // ── Close dropdown on outside click ──
  useEffect(() => {
    const close = (e) => { if (!e.target.closest('.dsp-actions-wrapper')) setActiveMenu(null); };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  // ── Functions ──
  const calculateStats = () => {
    const total = disputes.length;
    const resolved = disputes.filter(d => d.status === 'resolved').length;
    const inProgress = disputes.filter(d => d.status === 'active').length;
    const urgent = disputes.filter(d => {
      const daysDiff = Math.floor((new Date() - new Date(d.date)) / (1000 * 60 * 60 * 24));
      return daysDiff > 3 && d.status === 'active';
    }).length;
    setStats({ total, resolved, inProgress, urgent });
  };

  // ── Fetch chat for selected dispute ──
  // ✅ FIX: Authorization moved from request body into the axios `headers` option.
  const fetchChatForDispute = (dispute) => {
    axios.post(`${baseUrl}/rentaladmin/dispute/fetchChatForDispute`, {
      srdispute_id: dispute.id
    }, {
      headers: { Authorization: `Bearer ${authToken}` }
    }).then((res) => {
      console.log("fetchChatForDispute:", res.data);
      if (res.data.success) {
        setChatMessages(res.data.chats || []);
        setConversationId(res.data.srconversation_id);
      } else {
        setChatMessages([]);
        setConversationId(null);
      }
    }).catch((err) => {
      console.log(err);
      setChatMessages([]);
      setConversationId(null);
    });
  };

  const handleOpenDispute = (dispute) => {
    setSelectedDispute(dispute);
    setShowModal(true);
    setNewMessage('');
    setActiveMenu(null);
    fetchChatForDispute(dispute);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedDispute(null);
    setNewMessage('');
    setChatMessages([]);
    setConversationId(null);
  };

  // ✅ FIX: Authorization moved from request body into the axios `headers` option.
  const viewCustomerDisputes = () => {
    axios.post(
      `${baseUrl}/rentaladmin/dispute/viewCustomerDisputes`,
      { srrentaladmin_id: loggedInRental.id },
      { headers: { Authorization: `Bearer ${authToken}` } }
    )
      .then((res) => {
        console.log("viewCustomerDisputes:", res.data);
        if (res.data.success) {
          setCustomerDisputes(res.data.request || []);
        }
      })
      .catch((err) => console.log(err));
  };

  // ── Send message ──
  // ✅ FIX: Authorization moved from request body into the axios `headers` option.
  const handleSendMessage = () => {
    if (!newMessage.trim() || !selectedDispute || !conversationId) return;

    axios.post(`${baseUrl}/rentaladmin/dispute/sendMessageForDispute`, {
      srconversation_id: conversationId,
      message: newMessage
    }, {
      headers: { Authorization: `Bearer ${authToken}` }
    }).then((res) => {
      console.log("sendMessage:", res.data);
      if (res.data.success) {
        setNewMessage('');
        fetchChatForDispute(selectedDispute);
      }
    }).catch((err) => console.log(err));
  };

  // ── Mark as resolved ──
  // ⚠️ NOTE: `/rentaladmin/dispute/markAsResolved` is not defined in the Laravel
  // routes file — this call will 404 regardless of the Authorization header.
  // Left as-is (no backend route change requested); Authorization still moved
  // to headers so it's consistent and ready once the route exists.
  const handleMarkAsResolved = () => {
    if (!selectedDispute) return;
    axios.post(`${baseUrl}/rentaladmin/dispute/markAsResolved`, {
      srdispute_id: selectedDispute.id
    }, {
      headers: { Authorization: `Bearer ${authToken}` }
    }).then((res) => {
      if (res.data.success) {
        setDisputes(disputes.map(d =>
          d.id === selectedDispute.id ? { ...d, status: 'resolved' } : d
        ));
        handleCloseModal();
      }
    }).catch((err) => console.log(err));
  };

  // ── Delete dispute ──
  const handleDeleteDispute = (id) => {
    setActiveMenu(null);
    if (window.confirm('Are you sure you want to delete this dispute?'))
      setDisputes(disputes.filter(d => d.id !== id));
  };

  // ── Forward dispute to superadmin ──
  const handleForwardToSuperadmin = (dispute) => {
    setActiveMenu(null);

    Swal.fire({
      title: 'Are you sure?',
      text: 'This dispute will be forwarded to the Superadmin for resolution.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, forward it',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#FDB714',
    }).then((result) => {
      if (!result.isConfirmed) return;

      setForwarding(true);

      axios.post(`${baseUrl}/rentaladmin/dispute/forwardToSuperadmin`, {
        srdispute_id: dispute.id
      }, {
        headers: { Authorization: `Bearer ${authToken}` }
      }).then((res) => {
        setForwarding(false);
        if (res.data.success) {
          const updater = (list) => list.map(d =>
            d.id === dispute.id ? { ...d, forwarded: true } : d
          );
          setDisputes(updater);
          setCustomerDisputes(updater);
          Swal.fire({
            title: 'Forwarded!',
            text: 'The dispute has been forwarded to the Superadmin.',
            icon: 'success',
            confirmButtonColor: '#FDB714',
          });
        } else {
          Swal.fire({
            title: 'Failed',
            text: res.data.message || 'Could not forward this dispute.',
            icon: 'error',
            confirmButtonColor: '#FDB714',
          });
        }
      }).catch((err) => {
        setForwarding(false);
        console.log(err);
        Swal.fire({
          title: 'Error',
          text: 'Something went wrong while forwarding this dispute.',
          icon: 'error',
          confirmButtonColor: '#FDB714',
        });
      });
    });
  };

  const getStatusStyle = (status) => {
    if (status === 'resolved') return { bg: '#DCFCE7', color: '#15803D' };
    if (status === 'active') return { bg: '#FEF9C3', color: '#A16207' };
    if (status === 'pending') return { bg: '#DBEAFE', color: '#1D4ED8' };
    return { bg: '#E5E7EB', color: '#374151' };
  };

  const formatTime = (datetime) => {
    if (!datetime) return '';
    return new Date(datetime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  // ── Sender label (for chat bubbles) ──
  const getSenderLabel = (sender) => {
    if (sender === 'rentaladmin') return 'You';
    if (sender === 'customer') return 'Customer';
    if (sender === 'superadmin') return 'Superadmin';
    return 'Admin';
  };

  // ──────────────────────────────────────────────────────────────
  // ✅ Single source of truth for "Raised By" / "Against" display.
  // Used in BOTH the table rows and the View Dispute modal, so they
  // always show the exact same value no matter which tab it's from.
  //
  // - If the dispute came from a customer (has srcustomer relation
  //   or a srcustomer_id), it's a "Customer Dispute":
  //     Raised By -> customer's name/email/image
  //     Against   -> this rental admin's own name/email/image
  // - Otherwise it's a dispute the rental admin raised themself
  //   against a mechanic ("My Dispute"):
  //     Raised By -> this rental admin's own name/email/image
  //     Against   -> mechanic's name/email/image (srmechanicadmin)
  // ──────────────────────────────────────────────────────────────
  const isCustomerDispute = (dispute) =>
    Boolean(dispute?.srcustomer || dispute?.srcustomer_id);

  // ✅ Returns { name, email, image } for "Raised By"
  const getRaisedByInfo = (dispute) => {
    if (!dispute) return { name: '—', email: '', image: null };

    if (isCustomerDispute(dispute)) {
      const cust = dispute.srcustomer;
      const fullName =
        cust?.fullname ||
        `${cust?.firstname || ''} ${cust?.lastname || ''}`.trim();

      return {
        name: fullName || dispute.raisedby || `Customer #${dispute.srcustomer_id || ''}`,
        email: cust?.email || '',
        image: cust?.image || null,
      };
    }

    return {
      name:
        loggedInRental?.businessname ||
        loggedInRental?.shopname ||
        loggedInRental?.fullname ||
        loggedInRental?.name ||
        (loggedInRental?.email ? loggedInRental.email.split('@')[0] : null) ||
        dispute.raisedby ||
        `Rental Admin #${loggedInRental?.id || ''}`,
      email: loggedInRental?.email || '',
      image: loggedInRental?.image || null,
    };
  };

  // ✅ Returns { name, email, image } for "Against"
  const getAgainstInfo = (dispute) => {
    if (!dispute) return { name: '—', email: '', image: null };

    if (isCustomerDispute(dispute)) {
      // ✅ Customer disputes are always filed against THIS rental admin —
      // always show the live logged-in rental admin's own name here.
      // Never fall back to dispute.raisedto (that's a stale value saved
      // at dispute-creation time and can be wrong/placeholder text like
      // "Admin #1").
      const rentalName =
        loggedInRental?.businessname ||
        loggedInRental?.shopname ||
        loggedInRental?.fullname ||
        loggedInRental?.name ||
        (loggedInRental?.email ? loggedInRental.email.split('@')[0] : null) ||
        `Rental Admin #${loggedInRental?.id || ''}`;

      return {
        name: rentalName,
        email: loggedInRental?.email || '',
        image: loggedInRental?.image || null,
      };
    }

    const mech = dispute.srmechanicadmin;
    return {
      name: mech?.fullname || dispute.raisedto || '—',
      email: mech?.email || '',
      image: mech?.image || null,
    };
  };

  // ================================================================
  // RETURN
  // ================================================================
  return (
    <RentalAdminLayout>
      <div className="dsp-page">

        {/* ── Page Header ── */}
        <div className="dsp-page-header">
          <div>
            <h1 className="dsp-page-title">Resolve Disputes</h1>
            <p className="dsp-page-subtitle">Manage and resolve user disputes</p>
          </div>
        </div>

        {/* ════ Stat Cards ════ */}
        <Row className="mb-4">
          {[
            { icon: <FaExclamationCircle />, value: stats.total, label: 'Total Disputes' },
            { icon: <FaCheckCircle />, value: stats.resolved, label: 'Resolved' },
            { icon: <FaClock />, value: stats.inProgress, label: 'In Progress' },
            { icon: <FaExclamationCircle />, value: stats.urgent, label: 'Urgent (3 days)' },
          ].map((s, i) => (
            <Col lg={3} md={6} className="mb-3" key={i}>
              <div className="dsp-stat-card">
                <div className="dsp-stat-icon">{s.icon}</div>
                <div className="dsp-stat-content"><h3>{s.value}</h3><p>{s.label}</p></div>
              </div>
            </Col>
          ))}
        </Row>

        {/* ════ Table ════ */}
        <div className="dsp-table-section">
          <div className="dsp-table-header">
            <Tab.Container activeKey={activeTab}>
              <Nav variant="tabs" className="dsp-tabs">
                <Nav.Item>
                  <Nav.Link
                    eventKey="myDisputes"
                    className="dsp-tab-link"
                    onClick={() => {
                      setActiveTab("myDisputes");
                      setCurrentPage(1);
                    }}
                  >
                    My Disputes
                  </Nav.Link>
                </Nav.Item>

                <Nav.Item>
                  <Nav.Link
                    eventKey="customerDisputes"
                    className="dsp-tab-link"
                    onClick={() => {
                      setActiveTab("customerDisputes");
                      setCurrentPage(1);
                    }}
                  >
                    Customer Disputes
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Tab.Container>
          </div>

          {/* ✅ Same column structure for both tabs now — Raised By / Against
              are resolved by getRaisedByInfo() / getAgainstInfo() regardless
              of which tab/source the dispute came from. Dispute ID column removed. */}
          <div className="dsp-table-wrapper">
            <table className="dsp-table">
              <thead>
                <tr>
                  <th>RAISED BY</th>
                  <th>AGAINST</th>
                  <th>CATEGORY</th>
                  <th>STATUS</th>
                  <th>DATE</th>
                  <th>FORWARDED</th>
                  <th className="dsp-col-action">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {currentDisputes.length > 0 ? currentDisputes.map((dispute) => {
                  const st = getStatusStyle(dispute.status);
                  const raisedBy = getRaisedByInfo(dispute);
                  const against = getAgainstInfo(dispute);
                  const isForwarded = !!dispute.forwarded;
                  return (
                    <tr key={dispute.id}>
                      <td>
                        <div className="dsp-seller-cell">
                          <img
                            src={`${baseUrlForFile}/${raisedBy.image}`}
                            alt={raisedBy.name}
                            className="dsp-seller-avatar"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <div className="dsp-seller-info">
                            <div className="dsp-seller-name">{raisedBy.name}</div>
                            <div className="dsp-seller-email">{raisedBy.email}</div>
                          </div>
                        </div>
                      </td>

                      <td>
                        <div className="dsp-seller-cell">
                          <img
                            src={`${baseUrlForFile}/${against.image}`}
                            alt={against.name}
                            className="dsp-seller-avatar"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <div className="dsp-seller-info">
                            <div className="dsp-seller-name">{against.name}</div>
                            <div className="dsp-seller-email">{against.email}</div>
                          </div>
                        </div>
                      </td>

                      <td>{dispute.category}</td>

                      <td>
                        <span
                          className="dsp-status-badge"
                          style={{ background: st.bg, color: st.color }}
                        >
                          <span className="dsp-badge-dot" style={{ background: st.color }}></span>
                          {dispute.status}
                        </span>
                      </td>

                      <td>{new Date(dispute.date).toLocaleDateString()}</td>

                      <td>
                        <span
                          className="dsp-status-badge"
                          style={
                            isForwarded
                              ? { background: '#DBEAFE', color: '#1E40AF' }
                              : { background: '#E5E7EB', color: '#374151' }
                          }
                        >
                          {isForwarded ? 'Yes' : 'No'}
                        </span>
                      </td>

                      <td className="dsp-actions-cell">
                        <div className="dsp-actions-wrapper">
                          <button
                            className="dsp-menu-btn"
                            onClick={() =>
                              setActiveMenu(activeMenu === dispute.id ? null : dispute.id)
                            }
                          >
                            <FiMoreVertical size={18} />
                          </button>

                          {activeMenu === dispute.id && (
                            <div className="dsp-dropdown">
                              <button
                                className="dsp-drop-item dsp-drop-resolve"
                                onClick={() => handleOpenDispute(dispute)}
                              >
                                <FiEye size={14} />
                                View Dispute
                              </button>

                              {!isForwarded && (
                                <button
                                  className="dsp-drop-item dsp-drop-resolve"
                                  disabled={forwarding}
                                  onClick={() => handleForwardToSuperadmin(dispute)}
                                >
                                  <FiSend size={14} />
                                  Forward to Superadmin
                                </button>
                              )}

                              <button
                                className="dsp-drop-item dsp-drop-delete"
                                onClick={() => handleDeleteDispute(dispute.id)}
                              >
                                <FiTrash2 size={14} />
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr><td colSpan="7" className="dsp-empty-row">No disputes found</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <AppPagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={currentData.length}
            itemsPerPage={disputesPerPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      </div>

      {/* ════════════════════════════════
          VIEW / RESOLVE DISPUTE MODAL
      ════════════════════════════════ */}
      {showModal && selectedDispute && (
        <div className="dsp-overlay" onClick={handleCloseModal}>
          <div className="dsp-modal" onClick={e => e.stopPropagation()}>

            <div className="dsp-modal-header">
              <div className="dsp-modal-header-left">
                <h5>View Dispute</h5>
                <span>Full breakdown of dispute #{selectedDispute.id}</span>
              </div>
              <button className="dsp-modal-close" onClick={handleCloseModal}><FiX /></button>
            </div>

            <div className="dsp-modal-body">

              {/* Status Bar */}
              <div className="dsp-modal-status-bar">
                <div className="dsp-sb-left">
                  <span className="dsp-sb-id">#{selectedDispute.id}</span>
                  <span className="dsp-status-badge"
                    style={{ background: getStatusStyle(selectedDispute.status).bg, color: getStatusStyle(selectedDispute.status).color }}>
                    <span className="dsp-badge-dot" style={{ background: getStatusStyle(selectedDispute.status).color }}></span>
                    {selectedDispute.status}
                  </span>
                  {selectedDispute.forwarded && (
                    <span className="dsp-status-badge" style={{ background: '#DBEAFE', color: '#1E40AF' }}>
                      Forwarded to Superadmin
                    </span>
                  )}
                </div>
                <span className="dsp-sb-date">{new Date(selectedDispute.date).toLocaleDateString()}</span>
              </div>

              {/* Raised By / Against — avatar strips, same baseUrlForFile as table */}
              <p className="dsp-section-title">Dispute Parties</p>
              <div className="dsp-parties-grid">
                <div className="dsp-party-card">
                  <label>Raised By</label>
                  <div className="dsp-seller-cell">
                    <img
                      src={`${baseUrlForFile}/${getRaisedByInfo(selectedDispute).image}`}
                      alt={getRaisedByInfo(selectedDispute).name}
                      className="dsp-seller-avatar dsp-seller-avatar-lg"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className="dsp-seller-info">
                      <div className="dsp-seller-name">{getRaisedByInfo(selectedDispute).name}</div>
                      <div className="dsp-seller-email">{getRaisedByInfo(selectedDispute).email}</div>
                    </div>
                  </div>
                </div>
                <div className="dsp-party-card">
                  <label>Against</label>
                  <div className="dsp-seller-cell">
                    <img
                      src={`${baseUrlForFile}/${getAgainstInfo(selectedDispute).image}`}
                      alt={getAgainstInfo(selectedDispute).name}
                      className="dsp-seller-avatar dsp-seller-avatar-lg"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className="dsp-seller-info">
                      <div className="dsp-seller-name">{getAgainstInfo(selectedDispute).name}</div>
                      <div className="dsp-seller-email">{getAgainstInfo(selectedDispute).email}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Info Grid */}
              <p className="dsp-section-title">Dispute Information</p>
              <div className="dsp-info-grid">
                <div className="dsp-info-item">
                  <label>Category</label>
                  <span>{selectedDispute.category || '—'}</span>
                </div>
                <div className="dsp-info-item">
                  <label>Date Filed</label>
                  <span>{new Date(selectedDispute.date).toLocaleDateString()}</span>
                </div>
                <div className="dsp-info-item" style={{ gridColumn: '1 / -1' }}>
                  <label>Description</label>
                  <span>{selectedDispute.description || '—'}</span>
                </div>
              </div>

              {/* Chat */}
              <p className="dsp-section-title">Dispute Resolution Chat</p>
              <div className="dsp-chat-box">
                <div className="dsp-chat-messages">
                  {chatMessages.length > 0 ? (
                    chatMessages.map((msg) => {
                      const isMe = msg.sender === 'rentaladmin';
                      return (
                        <div key={msg.id} className={`dsp-chat-msg ${isMe ? 'user' : 'admin'}`}>
                          <div className={`dsp-msg-bubble ${isMe ? 'user' : 'admin'}`}>
                            <div className="dsp-msg-sender">{getSenderLabel(msg.sender)}</div>
                            <div className="dsp-msg-text">{msg.message}</div>
                            <div className="dsp-msg-time">{formatTime(msg.created_at)}</div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="dsp-chat-empty">No messages yet. Start the conversation!</div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <div className="dsp-chat-input-area">
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Type your message..."
                    value={newMessage}
                    onChange={e => setNewMessage(e.target.value)}
                    onKeyPress={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    className="dsp-chat-textarea"
                  />
                  <button
                    className="dsp-send-btn"
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || !conversationId}
                  >
                    <FaPaperPlane size={14} /> Send
                  </button>
                </div>
              </div>
            </div>

            <div className="dsp-modal-footer">
              <button
                className="dsp-mf-btn dsp-mf-resolve"
                onClick={handleMarkAsResolved}
                disabled={selectedDispute.status === 'resolved'}
              >
                <FiCheckCircle size={14} /> Mark as Resolved
              </button>
              <button className="dsp-mf-btn dsp-mf-close" onClick={handleCloseModal}>Cancel</button>
            </div>
          </div>
        </div>
      )}

    </RentalAdminLayout>
  );
};

export default ResolveDisputes;